import {TagData, TagifySettings} from '@yaireo/tagify';
import {escape, isString} from 'lodash';

import {isDefined} from '../../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {placeCaretAtEnd} from '../../../../../helpers/domSelectionHelpers';
import {
  hasRegexDisclosure,
  isRegexPart,
  RegexPart,
  StringOrTemplate,
  StringTemplatePart,
} from '../../../../editors/commons/stringTemplateModel';
import {
  getLabelForTemplatePart,
  variableMissingError,
} from '../../../../editors/expressions/expressionLabels';
import {Expression} from '../../../../editors/expressions/expressionModel';
import {ExpressionRef} from '../../../../editors/expressions/expressionRefModel';
import {normalizeStringTemplate} from '../../../stringTemplateHelpers';

// Use as insertion point so the caret lands in a valid position.
export const ZERO_WIDTH_SPACE = '\u200B';

// With the default interpolator ("[[" "]]""), tagify can get confused if you use "[" manually.
// Replace with rare characters that users are unlikely to choose.
export const INTERPOLATOR: [string, string] = ['⊂⊂⊑⊑§§', '§§⊒⊒⊃⊃'];

export const TAGIFY_SETTINGS: TagifySettings<TemplatePartData> = {
  duplicates: true,
  autoComplete: {enabled: false},
  a11y: {focusableTags: true},
  editTags: false,
  mixMode: {
    insertAfterTag: ZERO_WIDTH_SPACE,
  },
  mixTagsInterpolator: INTERPOLATOR,
};

export interface TemplatePartData extends TagData {
  source: ExpressionRef | RegexPart;
  isValid: boolean;
  isRegex: boolean;
  hasDisclosure: boolean;
}

export function renderTagify(template: StringOrTemplate | undefined, expressions: ReadonlyArray<Expression>) {
  if (!template) {
    return '';
  }

  if (isString(template)) {
    return escape(template);
  }

  return template.template
    .map((part) => {
      if (isString(part)) {
        return escape(part);
      }

      return (
        INTERPOLATOR[0] +
        JSON.stringify(renderTemplatePart(part, expressions)) +
        INTERPOLATOR[1] +
        ZERO_WIDTH_SPACE
      );
    })
    .join('');
}

export function renderTemplatePart(
  part: ExpressionRef | RegexPart,
  expressions: ReadonlyArray<Expression>,
): TemplatePartData {
  const value = getLabelForTemplatePart(part, expressions);
  const isValid = isDefined(value);
  const isRegex = isRegexPart(part);
  const hasDisclosure = isRegex && hasRegexDisclosure(part);

  return {
    value: value ?? variableMissingError,
    source: part,
    isValid,
    isRegex,
    hasDisclosure,
  };
}

// Parse tagify tokens inside a string.
export function parseTagifyValue(value: string): StringOrTemplate {
  const tokenRe = new RegExp(`${INTERPOLATOR[0]}(.+?)${INTERPOLATOR[1]}`, 'g');
  const template: StringTemplatePart[] = [];

  let previousIndex = 0;
  let result = tokenRe.exec(value);

  while (result) {
    if (result.index > previousIndex) {
      template.push(value.slice(previousIndex, result.index));
    }

    template.push(parseTemplatePart(result[1]));

    previousIndex = tokenRe.lastIndex;
    result = tokenRe.exec(value);
  }

  if (previousIndex < value.length - 1) {
    template.push(value.slice(previousIndex));
  }

  // Remove insertion points.
  const stripped = template.map((part) => (isString(part) ? stripZeroWidthSpaces(part) : part));

  return normalizeStringTemplate(stripped);
}

function parseTemplatePart(rawValue: string) {
  try {
    const part = JSON.parse(rawValue) as TemplatePartData;
    return part.source;
  } catch (e) {
    return INTERPOLATOR[0] + rawValue + INTERPOLATOR[1];
  }
}

// Since the editor is not necessarily in focus when a tag is added, tagify stores the last known selection.
// If no selection is available, it incorrectly places the caret after the trailing <BR> which breaks the component.
export function maybeResetSelection(tagify: Tagify<TemplatePartData>) {
  const hasStateSelection = Boolean((tagify as any).state.selection);

  if (hasStateSelection) {
    return;
  }

  placeCaretAtEnd(tagify.DOM.input);
  (tagify as any).setStateSelection();
}

// Zero-width spaces are used to provide insertion points.
export function stripZeroWidthSpaces(value: string) {
  return value.replace(new RegExp(ZERO_WIDTH_SPACE, 'g'), '');
}
